import { needsSession } from '$lib/config/mode';
import type { ApplicationMode } from '$lib/stores/mode';
import type { StepName } from '$lib/stores/guided';

/**
 * What the customer is looking at, as one value.
 *
 * # Why this exists
 *
 * It replaces eleven derived booleans in the layout — `wizardHasTheScreen`,
 * `dataViewHasTheScreen`, `onTheDoor`, `selectionIsOnScreen`,
 * `memberHomeHasTheScreen`, `savedListHasTheScreen`, `memberChromeHasTheScreen`
 * and four route tests — each computed from its own subset of nine stores.
 * There was no single value saying where you were, only a set of overlapping
 * opinions that had to be kept consistent by hand.
 *
 * The cost of that was not theoretical. Two bugs in one session came from two
 * conditions disagreeing about what was on screen: `Results` drew the members'
 * home over the top of the step a door had just set, and `FilterBar` reopened
 * the industry picker over a search that was already there. Both are
 * unrepresentable here, because there is one answer.
 *
 * It also changes what adding a screen costs. Before, a new screen had to be
 * *negotiated* with every existing one — the saved list needed a clause added
 * to `memberHomeHasTheScreen`, a `closeSavedList()` added to two navigation
 * helpers, and a new derivation for the sidebar. Now a screen is one more
 * member of the union and one more line in `sidebarFor`.
 *
 * # Why a pure function rather than a derived store
 *
 * Two reasons, both practical. The route is one of the inputs, and
 * `$app/stores` does not resolve in the test build — `vitest.config.ts` leaves
 * SvelteKit out on purpose, so a store that imported it could not be tested at
 * all. And one of the inputs (`restoringShare`) is a local in the layout.
 *
 * A function takes what it needs and can be exercised over every combination,
 * which is what makes the precedence below something we can assert rather than
 * something we hope survives the next edit.
 */

/** Where the customer is. */
export type Screen =
  /** The members' build, with no session yet. Draws nothing and redirects. */
  | { kind: 'session' }

  /** A route that draws its own frame: sign-in, checkout, the Stripe return. */
  | { kind: 'bare' }

  /** One company. Keeps the shell, loses the filter panel. */
  | { kind: 'profile' }

  /** The order form. */
  | { kind: 'checkout' }

  /** The industry picker. */
  | { kind: 'picker' }

  /** The picker, showing one category in detail. */
  | { kind: 'industryProfile' }

  /** A shared selection being rebuilt; the search is already running. */
  | { kind: 'restoringShare' }

  /** One saved list. */
  | { kind: 'list' }

  /** What this organisation has exported. */
  | { kind: 'exportHistory' }

  /** The assistant. */
  | { kind: 'assistant' }

  /** The members' front screen: the search box and the doors. */
  | { kind: 'home' }

  /**
   * The guided search.
   *
   * `atDoor` is the first question — "how would you like to start" — which is
   * the beginning rather than the middle. It carries its own field because the
   * old `onTheDoor` needed both halves and got it wrong on its own: the step
   * stays at 'door' for anybody who never used the guide, so testing the step
   * alone hid the start-over button on the data view, which is exactly where
   * somebody with a selection to abandon is standing.
   */
  | { kind: 'guide'; atDoor: boolean }

  /** Classic mode's data view: the filter bar and the rows. */
  | { kind: 'results' }

  /** The similar-companies screen. */
  | { kind: 'neighbours' }

  /** The datapool screen. */
  | { kind: 'datapools' }

  /** A mode nothing draws. Kept explicit so the union is total. */
  | { kind: 'unknown' };

/** Everything the answer depends on. */
export interface ScreenInput {
  /** The current path. */
  path: string;

  /** Whether this build has accounts and members' screens. See AppMode. */
  memberApp: boolean;

  signedIn: boolean;
  mode: ApplicationMode;

  /** The assistant is up. */
  aiActive: boolean;

  /** The industry picker is up. */
  picker: boolean;

  /** The picker is showing one category. */
  industryProfile: boolean;

  restoringShare: boolean;

  /** A saved list is open. */
  listOpen: boolean;

  /** The export history is open. */
  historyOpen: boolean;

  guideActive: boolean;
  guideStep: StepName;

  /** How many companies the selection holds. */
  companyCount: number;
}

/**
 * Which screen the app is on.
 *
 * # The order is the contract
 *
 * This is the precedence the layout's branch chain had, written once instead of
 * twice. It ran in two places with *different* orders — the content area tested
 * the route first, the sidebar tested the members' chrome first — which is why
 * a company profile opened while a saved list was open drew the profile with
 * the list's navigation beside it. There is one order now, and it is this one.
 */
export function screenFor(input: ScreenInput): Screen {
  // Before anything else: the members' build shows nothing at all without a
  // session, because its components call the API as they mount. See
  // docs/refactor/two-deployments.md.
  if (input.memberApp && !input.signedIn && needsSession(input.path)) {
    return { kind: 'session' };
  }

  // Routes that draw their own frame, and are reachable signed out.
  if (
    input.path === '/thank-you' ||
    input.path === '/login' ||
    input.path.startsWith('/checkout/')
  ) {
    return { kind: 'bare' };
  }

  if (input.path.startsWith('/company/')) return { kind: 'profile' };

  if (input.mode === 'checkout') return { kind: 'checkout' };

  // The picker is a modal over whatever was there, so it outranks the modes.
  if (input.picker) {
    return input.industryProfile ? { kind: 'industryProfile' } : { kind: 'picker' };
  }

  if (input.restoringShare) return { kind: 'restoringShare' };

  // A list is a place the customer navigated to, not a view of what they were
  // looking at, so it takes the screen from a selection as well as from the
  // front door.
  if (input.memberApp && input.listOpen) return { kind: 'list' };

  // Beside the list, and for the same reason: somewhere the customer navigated
  // to rather than a view of what they were looking at.
  if (input.memberApp && input.historyOpen) return { kind: 'exportHistory' };

  if (input.mode === 'classic') {
    if (input.aiActive) return { kind: 'assistant' };

    // The guide stands in front of an empty selection. Both halves matter:
    // `guideActive` stays true through a search started from the assistant,
    // which never passes the guide's own exit, so the flag alone would keep
    // the guide over a screen full of results.
    if (input.guideActive && input.companyCount === 0) {
      const atDoor = input.guideStep === 'door';
      // The members' front screen replaces the guide's *door step* only. From
      // the first question onwards the guide runs for everybody.
      if (input.memberApp && atDoor) return { kind: 'home' };
      return { kind: 'guide', atDoor };
    }

    return { kind: 'results' };
  }

  if (input.mode === 'neighbor') return { kind: 'neighbours' };
  if (input.mode === 'dataselection') return { kind: 'datapools' };

  return { kind: 'unknown' };
}

/** What stands to the left of the content. */
export type Sidebar = 'none' | 'filters' | 'memberNav';

/**
 * Which sidebar a screen wants.
 *
 * One line per screen, which is the point: the sidebar used to be decided by a
 * second condition chain that had to agree with the first, and adding a screen
 * meant remembering to edit both.
 *
 * Written as an exhaustive switch so that TypeScript refuses a new screen that
 * forgot to say. That refusal is the whole safety property — a missing case
 * here was previously a screen that silently drew the wrong furniture.
 */
export function sidebarFor(screen: Screen): Sidebar {
  switch (screen.kind) {
    // The members' navigation: the lists, and the doors.
    case 'home':
    case 'list':
    case 'exportHistory':
      return 'memberNav';

    // Full-width screens. The assistant, the guide, the order form and the
    // picker each ask one thing at a time, and a filter panel beside them is a
    // second way to answer the question being asked. The profile is not a
    // question at all — it is one company, and the panel narrows a search that
    // is not on screen.
    case 'profile':
    case 'checkout':
    case 'picker':
    case 'industryProfile':
    case 'assistant':
    case 'guide':
      return 'none';

    // Screens with a selection to narrow.
    case 'results':
    case 'neighbours':
    case 'datapools':
    case 'restoringShare':
      return 'filters';

    // No shell at all.
    case 'session':
    case 'bare':
    case 'unknown':
      return 'none';
  }
}

/**
 * Whether there is a selection on screen worth handing to somebody.
 *
 * Wider than the data view: a similarity search puts results on screen too, and
 * they are as shareable as any others. Gating the share button on the narrower
 * test left it missing in the mode whose selections are hardest to rebuild by
 * hand.
 */
export function showsSelection(screen: Screen): boolean {
  return screen.kind === 'results' || screen.kind === 'neighbours';
}

/** Whether classic mode's data view is what is on screen. */
export function isDataView(screen: Screen): boolean {
  return screen.kind === 'results';
}

/**
 * Whether the screen is a front door rather than work in progress.
 *
 * The members' home and the guide's first question are the same moment: the
 * beginning, with nothing yet to abandon.
 */
export function isFrontDoor(screen: Screen): boolean {
  return screen.kind === 'home' || (screen.kind === 'guide' && screen.atDoor);
}

/**
 * Whether the price bar may offer to order.
 *
 * Exactly what the old expression said — `!wizardHasTheScreen &&
 * !showDataSelectionModal` — written as a screen test so it can be read. The
 * four screens it refuses are the two front doors and the two states of the
 * picker: places where the customer is choosing what to look for rather than
 * looking at something they could buy.
 *
 * Note that a saved list answers true, which is inherited rather than
 * intended: the bar offers to order "the selection" while the screen is
 * showing a list, which is not the same set of rows. It is preserved here
 * because changing behaviour inside a refactor is how a refactor stops being
 * checkable — and it is now a one-line change when somebody decides.
 */
export function canOrder(screen: Screen): boolean {
  switch (screen.kind) {
    case 'home':
    case 'guide':
    case 'picker':
    case 'industryProfile':
      return false;
    default:
      return true;
  }
}

/** Whether a front screen is up in place of the data view. */
export function isFrontScreen(screen: Screen): boolean {
  return screen.kind === 'home' || screen.kind === 'guide';
}
